import React from 'react';
import {
  Button, FilterChip, FormField, JalaliDatePicker, SearchInput, SegmentedControl,
} from '@parsina/framework';
import { FileSpreadsheet, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useShortcut } from '../../lib/shortcuts';
import { STATUS_FILTERS, statusLabelKey, tierLabelKey } from '../../constants/subscription';
import { TIER_FILTERS } from '../../lib/clinicsView';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';

/**
 * Zone ④ — the filter toolbar.
 *
 * ── STATUS IS A CHIP ROW, TIER IS A SEGMENTED CONTROL ───────────────────────
 * Not a stylistic split. A chip row wraps and carries a COUNT, which is what the
 * status filter needs: seven options, and «how many are expired» is the question
 * an operator is asking when they reach for it. The tier filter has four options,
 * no counts, and is genuinely a small set of alternatives. Two `SegmentedControl`s
 * side by side (the previous shape) overflowed at Compact width and gave the
 * status filter no room for the two states — PAST_DUE and SUSPENDED — that were
 * missing from it entirely.
 *
 * ── FILTERS APPLY IMMEDIATELY ───────────────────────────────────────────────
 * §9.2: no «اعمال فیلتر» button. Search debounces (the input still echoes every
 * keystroke instantly); everything else applies on change. The «جست‌وجو» button
 * this replaces made the search look like it had not worked until you pressed it.
 *
 * ── THE REGISTRATION RANGE AND THE EXPORT ──────────────────────────────────
 * Two Jalali pickers narrow the list to the clinics registered between two days
 * (both ends inclusive), and «خروجی اکسل» writes WHAT IS LEFT — every filtered
 * row, not the page on screen. The export lives here and not in the page header
 * because it is an action on the current view, and the header's one slot belongs
 * to provisioning. It is disabled on an empty result: an empty file is not an
 * answer to «who registered on these days».
 *
 * ── THE SEARCH BOX IS THE KEYBOARD ENTRY POINT ──────────────────────────────
 * `F3` and `/` focus it, `Esc` clears it, both through the shortcut registry so
 * they are listed in the `?` overlay and cannot collide with another screen's
 * binding.
 */
export default function ClinicsToolbar({
  view, counts, onChange, onClear, isFiltered, resultLabel, onExport, exportCount = 0,
}) {
  const { t, formatNumber } = useTranslation();
  const searchRef = React.useRef(null);

  // The field is LOCAL state so it echoes instantly, and the URL is written after
  // the debounce: putting every keystroke in the URL would either lag the caret
  // or fill the history with twenty entries.
  const [draft, setDraft] = React.useState(view.q || '');

  // Adopt an externally-changed query — a «حذف فیلترها», or arriving on a deep
  // link — without fighting what the operator is typing.
  React.useEffect(() => { setDraft(view.q || ''); }, [view.q]);

  React.useEffect(() => {
    if (draft === (view.q || '')) return undefined;
    const timer = setTimeout(() => onChange({ q: draft, page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, view.q, onChange]);

  const focusSearch = () => {
    searchRef.current?.focus();
    searchRef.current?.select?.();
  };

  useShortcut('f3', focusSearch, {
    label: t('shortcuts.action.search'),
    group: t('shortcuts.groups.clinics'),
  });
  // A bare printable key, so the registry's caret rule keeps it from firing while
  // the operator is typing into a field — «/» inside the search box is a slash.
  useShortcut('/', focusSearch, {
    label: t('shortcuts.action.search'),
    group: t('shortcuts.groups.clinics'),
  });

  return (
    <div data-testid="clinics-toolbar">
      <div className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        {/* `role="group"`, not `radiogroup`: `FilterChip` reports its state with
            `aria-pressed`, which makes each chip a toggle BUTTON, and a radiogroup
            whose children are not radios announces a control that is not there.
            The group + label is what tells a screen reader these belong together.
            The counts are formatted here because the kit takes `count` as a node —
            the digit seam belongs to the app, which knows the locale. */}
        <div
          role="group"
          aria-label={t('clinics.filters.label')}
          className="flex flex-wrap items-center gap-1.5"
          data-testid="clinics-filter"
        >
          {STATUS_FILTERS.map((value) => (
            <FilterChip
              key={value}
              label={value === 'ALL' ? t('clinics.filters.all') : t(statusLabelKey(value))}
              count={counts[value] == null ? undefined : formatNumber(counts[value])}
              active={view.status === value}
              onClick={() => onChange({ status: value, page: 1 })}
              data-testid={`clinics-filter-${value}`}
            />
          ))}
        </div>

        <SegmentedControl
          semantics="filter"
          size="sm"
          ariaLabel={t('clinics.filters.tierLabel')}
          options={TIER_FILTERS.map((value) => ({
            value,
            label: value === 'ALL' ? t('clinics.filters.allTiers') : t(tierLabelKey(value)),
          }))}
          value={view.tier}
          onChange={(tier) => onChange({ tier, page: 1 })}
          data-testid="clinics-tier-filter"
        />

        <FormField label={t('clinics.filters.from')}>
          <div className="w-44" data-testid="clinics-from">
            <JalaliDatePicker
              value={view.from || ''}
              onChange={(v) => onChange({ from: v || '', page: 1 })}
              placeholder={t('clinics.filters.datePlaceholder')}
              disableFuture
            />
          </div>
        </FormField>
        <FormField label={t('clinics.filters.to')}>
          <div className="w-44" data-testid="clinics-to">
            <JalaliDatePicker
              value={view.to || ''}
              onChange={(v) => onChange({ to: v || '', page: 1 })}
              placeholder={t('clinics.filters.datePlaceholder')}
              disableFuture
            />
          </div>
        </FormField>

        <div className="min-w-[16rem] flex-1">
          <SearchInput
            ref={searchRef}
            value={draft}
            onChange={(e) => setDraft(e.target?.value ?? e)}
            onKeyDown={(e) => {
              // Esc clears rather than blurring: an operator who mistyped wants
              // the field empty, and the browser's own Esc behaviour on a search
              // input is inconsistent between engines.
              if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); }
            }}
            placeholder={t('clinics.search.placeholder')}
            aria-label={t('clinics.search.label')}
            data-testid="clinics-search"
          />
        </div>

        {/* Only when there is something to clear — a permanently-visible «clear»
            on an unfiltered list is a control that does nothing. */}
        {isFiltered ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            data-testid="clinics-clear-filters"
          >
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}

        <Button
          variant="outline"
          size="sm"
          onClick={onExport}
          disabled={!onExport || exportCount < 1}
          title={t('clinics.export.hint', { count: exportCount })}
          data-testid="clinics-export"
        >
          <FileSpreadsheet size={14} aria-hidden />
          {t('clinics.export.button')}
        </Button>
      </div>

      {/* How much the filters left, announced — a filter that silently returns
          three rows out of four hundred should say so. */}
      <p
        className="mb-3 text-xs text-muted-foreground"
        aria-live="polite"
        data-testid="clinics-count"
      >
        {resultLabel}
      </p>
    </div>
  );
}
